import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, Package, Warning, Trash, Lightning, ArrowCounterClockwise, ArrowsClockwise } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateFormat';
import { SearchFilterBar, EmptySearchResult, LoadErrorState } from '../components/SearchFilterBar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

const STATUS_CONFIG = {
  procurement_approved: { label: 'Procurement Approved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  procurement_rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-200' },
  assigned_to_production: { label: 'Assigned to Production', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  under_production: { label: 'Under Production', className: 'bg-purple-50 text-purple-700 border-purple-200' },
  production_on_hold: { label: 'Production On Hold', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  production_completed: { label: 'Production Completed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  scheduled_for_dispatch: { label: 'Scheduled for Dispatch', className: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  ready_to_dispatch: { label: 'Ready to Dispatch', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  dispatched: { label: 'Dispatched', className: 'bg-zinc-50 text-zinc-700 border-zinc-200' },
  partially_dispatched: { label: 'Partially Dispatched', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  pending_for_installation: { label: 'Pending Installation', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  installation_completed: { label: 'Installation Completed', className: 'bg-teal-50 text-teal-700 border-teal-200' },
  revoked_by_admin: { label: 'Revoked by Admin', className: 'bg-rose-100 text-rose-800 border-rose-300' }
};

const PRIORITY_THRESHOLDS = { HIGH: 8, MEDIUM: 4 };
const PRIORITY_MAP = { 'High': 10, 'Medium': 5, 'Low': 1 };
const ADVANCED_STATUSES = ['under_production', 'production_on_hold', 'production_completed', 'ready_to_dispatch', 'scheduled_for_dispatch', 'dispatched'];

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || { label: status, className: 'bg-zinc-50 text-zinc-700' };
  return <Badge className={`${config.className} border`}>{config.label}</Badge>;
}

const PRIORITY_STYLES = {
  high: { label: 'High', color: 'bg-red-50 text-red-700 border-red-200' },
  medium: { label: 'Medium', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  low: { label: 'Low', color: 'bg-blue-50 text-blue-700 border-blue-200' }
};

function getPriorityLevel(priority) {
  if (priority >= PRIORITY_THRESHOLDS.HIGH) return 'high';
  if (priority >= PRIORITY_THRESHOLDS.MEDIUM) return 'medium';
  return 'low';
}

function PriorityBadge({ priority }) {
  if (!priority || priority === 0) return null;
  const { label, color } = PRIORITY_STYLES[getPriorityLevel(priority)];
  return <Badge className={`${color} border`}>{label} Priority</Badge>;
}

function getCardBorder(isRejected, hasUrgent, isUrgent, isRevoked) {
  if (isRevoked) return 'border-2 border-rose-400 bg-rose-50/30';
  if (isUrgent) return 'border-2 border-red-500 urgent-card-pulse';
  if (isRejected) return 'border-2 border-red-300 bg-red-50/30';
  if (hasUrgent) return 'border-2 border-red-400';
  return 'border-zinc-300';
}

function ApprovedJobCard({ jo, isAdmin, isSelected, onSelect, onView, onDelete, onMarkUrgent, onUnmarkUrgent, onRevoke, onRestore, onReassign }) {
  const isRejected = jo.status === 'procurement_rejected';
  const isRevoked = jo.status === 'revoked_by_admin';
  const isProductionStage = ['assigned_to_production', 'under_production', 'production_on_hold'].includes(jo.status);
  const canReassign = isProductionStage || jo.status === 'production_completed';
  const cardBorder = getCardBorder(isRejected, jo.has_urgent_request, jo.is_urgent, isRevoked);

  return (
    <Card
      data-testid={`approved-job-${jo.jo_number}`}
      className={`rounded-sm shadow-sm ${cardBorder}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {isAdmin && jo.status === 'procurement_approved' && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={onSelect}
              className="mt-1 h-5 w-5"
              data-testid={`select-${jo.jo_number}`}
            />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h3 className="text-xl font-bold text-zinc-950">JO #{jo.jo_number}</h3>
              <StatusBadge status={jo.status} />
              <PriorityBadge priority={jo.priority} />
              {jo.has_urgent_request && (
                <Badge className="bg-red-100 text-red-800 border border-red-300">
                  <Warning size={14} weight="bold" className="mr-1" />
                  Exec Urgent
                </Badge>
              )}
              {jo.is_urgent && (
                <Badge
                  data-testid={`urgent-badge-${jo.jo_number}`}
                  className="bg-red-600 text-white border border-red-700 urgent-blink"
                >
                  <Lightning size={14} weight="bold" className="mr-1" />
                  URGENT (Admin)
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-sm">
              <div>
                <span className="text-zinc-500 font-bold uppercase text-xs">Customer:</span>
                <p className="text-zinc-950">{jo.customer_name}</p>
              </div>
              <div>
                <span className="text-zinc-500 font-bold uppercase text-xs">Executive:</span>
                <p data-testid={`approved-executive-${jo.jo_number}`} className="text-zinc-950">{jo.executive_name || '—'}</p>
              </div>
              <div>
                <span className="text-zinc-500 font-bold uppercase text-xs">Delivery Date:</span>
                <p className="text-zinc-950 font-bold text-red-600">{formatDate(jo.delivery_date) || jo.delivery_date}</p>
              </div>
              <div>
                <span className="text-zinc-500 font-bold uppercase text-xs">Windows:</span>
                <p className="text-zinc-950">{jo.no_of_windows} units</p>
              </div>
              <div>
                <span className="text-zinc-500 font-bold uppercase text-xs">Total Sqft:</span>
                <p className="text-zinc-950">{jo.total_sqft}</p>
              </div>
            </div>
            {isRejected && jo.procurement_remarks && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-sm px-3 py-2">
                <span className="text-xs font-bold uppercase text-red-500">Rejection Reason:</span>
                <p className="text-sm text-red-700 mt-0.5">{jo.procurement_remarks}</p>
              </div>
            )}
            {isRevoked && (
              <div className="mt-3 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2">
                <span className="text-xs font-bold uppercase text-rose-600">Revoked from Production:</span>
                <p className="text-sm text-rose-800 mt-0.5">{jo.revoke_reason || 'Revoked by admin.'} (was {jo.previous_status_before_revoke?.replace(/_/g,' ')})</p>
              </div>
            )}
            {jo.is_urgent && jo.urgent_reason && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-sm px-3 py-2">
                <span className="text-xs font-bold uppercase text-red-600">Urgent reason:</span>
                <p className="text-sm text-red-800 mt-0.5">{jo.urgent_reason}</p>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Button data-testid={`view-approved-${jo.jo_number}`} onClick={onView} variant="outline" className="h-10 w-full md:w-auto">
              <Eye size={18} weight="bold" className="mr-2" /> View
            </Button>
            {isAdmin && isProductionStage && (
              <Button
                data-testid={`revoke-${jo.jo_number}`}
                onClick={onRevoke}
                variant="outline"
                className="h-10 w-full md:w-auto border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                <ArrowCounterClockwise size={18} weight="bold" className="mr-2" /> Revoke
              </Button>
            )}
            {isAdmin && canReassign && (
              <Button
                data-testid={`reassign-${jo.jo_number}`}
                onClick={onReassign}
                variant="outline"
                className="h-10 w-full md:w-auto border-purple-300 text-purple-700 hover:bg-purple-50"
              >
                <ArrowsClockwise size={18} weight="bold" className="mr-2" /> Re-assign
              </Button>
            )}
            {isAdmin && isRevoked && (
              <Button
                data-testid={`restore-${jo.jo_number}`}
                onClick={onRestore}
                variant="outline"
                className="h-10 w-full md:w-auto border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <ArrowCounterClockwise size={18} weight="bold" className="mr-2" /> Restore
              </Button>
            )}
            {isAdmin && (isProductionStage || jo.status === 'procurement_approved') && (
              jo.is_urgent ? (
                <Button
                  data-testid={`unmark-urgent-${jo.jo_number}`}
                  onClick={onUnmarkUrgent}
                  variant="outline"
                  className="h-10 w-full md:w-auto border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                >
                  Clear Urgent
                </Button>
              ) : (
                <Button
                  data-testid={`mark-urgent-${jo.jo_number}`}
                  onClick={onMarkUrgent}
                  variant="outline"
                  className="h-10 w-full md:w-auto border-red-300 text-red-700 hover:bg-red-50"
                >
                  <Lightning size={18} weight="bold" className="mr-2" /> Mark Urgent
                </Button>
              )
            )}
            {isAdmin && (
              <Button data-testid={`delete-approved-${jo.jo_number}`} onClick={onDelete} variant="outline" className="h-10 w-full md:w-auto border-red-300 text-red-600 hover:bg-red-50">
                <Trash size={18} weight="bold" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DeleteJobDialog({ target, onClose, onConfirm, loading }) {
  if (!target) return null;
  return (
    <Dialog open={!!target} onOpenChange={onClose}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash size={24} weight="bold" /> Delete Job Order
          </DialogTitle>
          <DialogDescription className="sr-only">Confirm deletion of this job order</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {ADVANCED_STATUSES.includes(target.status) && (
            <div className="bg-amber-50 border border-amber-300 rounded-sm p-4">
              <div className="flex items-start gap-2">
                <Warning size={20} weight="bold" className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 font-bold">
                  This Job Order is already in an advanced stage. Deletion may affect records.
                </p>
              </div>
            </div>
          )}
          <p className="text-sm text-zinc-700">Are you sure you want to delete this Job Order? This action cannot be undone.</p>
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-2">
            {[
              ['Job Order No', target.jo_number, true],
              ['Customer Name', target.customer_name],
              ['Delivery Date', target.delivery_date],
              ['Current Status', target.status?.replace(/_/g, ' ')]
            ].map(([label, value, bold]) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-zinc-500 font-bold">{label}:</span>
                <span className={`text-zinc-950 ${bold ? 'font-bold' : ''} capitalize`}>{value}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button data-testid="confirm-delete-button" onClick={onConfirm} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white">
              {loading ? 'Deleting...' : 'Confirm Delete'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TotalsBar({ count, totalWindows, totalSqft }) {
  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Totals ({count} orders)</p>
          <div className="flex gap-8">
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
              <p data-testid="approved-total-windows" className="text-2xl font-bold text-zinc-950">{totalWindows}</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft</p>
              <p data-testid="approved-total-sqft" className="text-2xl font-bold text-zinc-950">{totalSqft.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const APPROVED_SEARCH_FIELDS = [
  { key: 'jo_number', label: 'JO Number', placeholder: 'Enter JO number' },
  { key: 'customer_name', label: 'Customer Name', placeholder: 'Enter customer name' },
  { key: 'customer_mobile', label: 'Customer Mobile', placeholder: 'Enter mobile number' },
  {
    key: 'status', label: 'Status', type: 'select',
    options: [
      { value: 'all', label: 'All Statuses' },
      { value: 'procurement_approved', label: 'Procurement Approved' },
      { value: 'procurement_rejected', label: 'Rejected' },
      { value: 'assigned_to_production', label: 'Assigned to Production' },
      { value: 'under_production', label: 'Under Production' },
      { value: 'production_on_hold', label: 'Production On Hold' },
      { value: 'production_completed', label: 'Production Completed' },
      { value: 'scheduled_for_dispatch', label: 'Scheduled for Dispatch' },
      { value: 'dispatched', label: 'Dispatched' },
      { value: 'revoked_by_admin', label: 'Revoked by Admin' },
    ]
  }
];

const APPROVED_DEFAULT_FILTERS = { jo_number: '', customer_name: '', customer_mobile: '', status: 'all' };

const ACTION_META = {
  urgent: { title: 'Mark Urgent Production', titleClass: 'text-red-700', btnClass: 'bg-red-600 hover:bg-red-700 text-white', btnLabel: 'Mark URGENT', url: 'mark-urgent', reasonHint: '(optional)', placeholder: 'e.g. Customer site ready Friday' },
  revoke: { title: 'Revoke from Production', titleClass: 'text-rose-700', btnClass: 'bg-rose-600 hover:bg-rose-700 text-white', btnLabel: 'Revoke', url: 'revoke-production', reasonHint: '(recommended)', placeholder: 'e.g. Wrong specs — needs re-approval' },
  reassign: { title: 'Re-assign to Production', titleClass: 'text-purple-700', btnClass: 'bg-purple-600 hover:bg-purple-700 text-white', btnLabel: 'Re-assign & Reset', url: 'reassign-production', reasonHint: '(recommended)', placeholder: 'e.g. Stages ticked without Start Production' },
};

function actionMessage(action, joNumber) {
  if (action === 'urgent') return `Mark JO #${joNumber} as URGENT. Production users will see a blinking alert.`;
  if (action === 'reassign') return `Re-assign JO #${joNumber} to Production. All stage ticks and start/end times will be cleared (a backup copy is kept) and the production team must click "Start Production" again. Delivery and planned dates are not changed.`;
  return `Revoke JO #${joNumber} from Production. It will be hidden from the production team until you restore it.`;
}

export function ApprovedJobs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [priority, setPriority] = useState('Medium');
  const [filters, setFilters] = useState(APPROVED_DEFAULT_FILTERS);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [actionTarget, setActionTarget] = useState(null); // {jo, action: 'urgent'|'revoke'}
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const isAdmin = user?.role === 'admin';

  const fetchApprovedJobs = useCallback(async (searchFilters) => {
    setLoading(true);
    setLoadError(false);
    try {
      const f = searchFilters || APPROVED_DEFAULT_FILTERS;
      const params = new URLSearchParams({ list_type: 'approved' });
      if (f.status && f.status !== 'all') params.append('status', f.status);
      if (f.jo_number) params.append('jo_number', f.jo_number);
      if (f.customer_name) params.append('customer_name', f.customer_name);
      if (f.customer_mobile) params.append('customer_mobile', f.customer_mobile);
      const { data } = await api.get(`/api/job-orders?${params}`);
      setJobOrders(data);
    } catch (err) {
      if (err?.isAuthError) return;
      setLoadError(true);
      toast.error('Failed to load job orders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApprovedJobs(); }, [fetchApprovedJobs]);

  const handleSearch = () => { fetchApprovedJobs(filters); };
  const handleReset = (cleared) => { setFilters(cleared); fetchApprovedJobs(cleared); };
  const handleRefresh = () => { fetchApprovedJobs(filters); };

  const handleSelectJob = (jo_number) => {
    setSelectedJobs(prev => prev.includes(jo_number) ? prev.filter(j => j !== jo_number) : [...prev, jo_number]);
  };

  const handleBulkAssign = async () => {
    if (selectedJobs.length === 0) { toast.error('Please select at least one job order'); return; }
    try {
      await api.post(`/api/job-orders/bulk-assign-production`,
        { jo_numbers: selectedJobs, priority: PRIORITY_MAP[priority] || 5 },
        { withCredentials: true });
      toast.success(`${selectedJobs.length} Job Orders assigned to production`);
      setSelectedJobs([]);
      fetchApprovedJobs();
    } catch { toast.error('Failed to assign job orders'); }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      if (deleteTarget.jo_number.includes('/')) {
        await api.post('/api/job-orders/delete-by-body', { jo_number: deleteTarget.jo_number });
      } else {
        await api.post(`/api/job-orders/${deleteTarget.jo_number}/delete`, {});
      }
      toast.success(`Job Order ${deleteTarget.jo_number} deleted`);
      setDeleteTarget(null);
      fetchApprovedJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally { setDeleteLoading(false); }
  };

  const openAction = (jo, action) => { setActionTarget({ jo, action }); setActionReason(''); };
  const closeAction = () => { setActionTarget(null); setActionReason(''); };

  const confirmAction = async () => {
    if (!actionTarget) return;
    const { jo, action } = actionTarget;
    setActionLoading(true);
    try {
      const url = `/api/job-orders/${encodeURIComponent(jo.jo_number)}/${ACTION_META[action].url}`;
      const { data } = await api.post(url, { reason: actionReason.trim() });
      const msg = { urgent: `${jo.jo_number} marked URGENT`, revoke: `${jo.jo_number} revoked from production`, reassign: data?.message || `${jo.jo_number} re-assigned to production` };
      toast.success(msg[action]);
      closeAction();
      fetchApprovedJobs(filters);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setActionLoading(false); }
  };

  const handleUnmarkUrgent = async (jo) => {
    try {
      await api.post(`/api/job-orders/${encodeURIComponent(jo.jo_number)}/unmark-urgent`, {});
      toast.success(`URGENT cleared on ${jo.jo_number}`);
      fetchApprovedJobs(filters);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to clear urgent'); }
  };

  const handleRestore = async (jo) => {
    try {
      await api.post(`/api/job-orders/${encodeURIComponent(jo.jo_number)}/restore-from-revoke`, {});
      toast.success(`${jo.jo_number} restored`);
      fetchApprovedJobs(filters);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to restore'); }
  };

  const { totalWindows, totalSqft } = useMemo(() => ({
    totalWindows: jobOrders.reduce((sum, jo) => sum + (jo.no_of_windows || 0), 0),
    totalSqft: jobOrders.reduce((sum, jo) => sum + (jo.total_sqft || 0), 0)
  }), [jobOrders]);

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">Approved & Rejected JO</h1>
            <p className="text-sm text-zinc-500 mt-2">View and manage approved and rejected job orders</p>
          </div>
          {isAdmin && selectedJobs.length > 0 && (
            <div className="flex items-center gap-3">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="w-40 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High Priority</SelectItem>
                  <SelectItem value="Medium">Medium Priority</SelectItem>
                  <SelectItem value="Low">Low Priority</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="bulk-assign-button" onClick={handleBulkAssign} className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11">
                <Package size={20} weight="bold" className="mr-2" /> Assign {selectedJobs.length} to Production
              </Button>
            </div>
          )}
        </div>

        <SearchFilterBar
          fields={APPROVED_SEARCH_FIELDS}
          filters={filters}
          setFilters={setFilters}
          onSearch={handleSearch}
          onReset={handleReset}
          onRefresh={handleRefresh}
          loading={loading}
          resultCount={jobOrders.length}
        />

        {loading && <div className="text-center py-12">Loading approved job orders...</div>}
        {!loading && loadError && <LoadErrorState onRetry={handleRefresh} />}
        {!loading && !loadError && jobOrders.length === 0 && (
          <EmptySearchResult onReset={() => handleReset(APPROVED_DEFAULT_FILTERS)} />
        )}
        {!loading && !loadError && jobOrders.length > 0 && (
          <>
            <div className="space-y-4">
              {jobOrders.map(jo => (
                <ApprovedJobCard
                  key={jo.jo_number}
                  jo={jo}
                  isAdmin={isAdmin}
                  isSelected={selectedJobs.includes(jo.jo_number)}
                  onSelect={() => handleSelectJob(jo.jo_number)}
                  onView={() => navigate(`/job-orders/${jo.jo_number}`)}
                  onDelete={() => setDeleteTarget(jo)}
                  onMarkUrgent={() => openAction(jo, 'urgent')}
                  onUnmarkUrgent={() => handleUnmarkUrgent(jo)}
                  onRevoke={() => openAction(jo, 'revoke')}
                  onRestore={() => handleRestore(jo)}
                  onReassign={() => openAction(jo, 'reassign')}
                />
              ))}
            </div>
            <TotalsBar count={jobOrders.length} totalWindows={totalWindows} totalSqft={totalSqft} />
          </>
        )}
      </div>

      <DeleteJobDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleteLoading} />

      <Dialog open={!!actionTarget} onOpenChange={(open) => { if (!open) closeAction(); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className={ACTION_META[actionTarget?.action]?.titleClass}>
              {ACTION_META[actionTarget?.action]?.title}
            </DialogTitle>
            <DialogDescription className="sr-only">Confirm the action for this Job Order</DialogDescription>
          </DialogHeader>
          {actionTarget && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-zinc-700">{actionMessage(actionTarget.action, actionTarget.jo.jo_number)}</p>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block">
                Reason {ACTION_META[actionTarget.action].reasonHint}
              </label>
              <textarea
                data-testid={`action-reason-${actionTarget.action}`}
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                rows={3}
                className="w-full border border-zinc-300 rounded p-2 text-sm"
                placeholder={ACTION_META[actionTarget.action].placeholder}
              />
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={closeAction} disabled={actionLoading}>Cancel</Button>
                <Button
                  data-testid={`confirm-${actionTarget.action}`}
                  onClick={confirmAction}
                  disabled={actionLoading}
                  className={ACTION_META[actionTarget.action].btnClass}
                >
                  {actionLoading ? 'Working...' : ACTION_META[actionTarget.action].btnLabel}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
